import { useEffect, useRef } from "react";
import overlayImage from "../assets/banner3.jpg";

export default function HomeBanner2() {
  const builds = [
    {
      title: "Mobile Applications",
      desc: "Custom Android and iOS apps designed for usability, speed, and real-world impact.",
    },
    {
      title: "Web Applications",
      desc: "Robust, browser-based systems tailored for businesses, startups, and organizations.",
    },
    {
      title: "Business Systems",
      desc: "ERP, CRM, booking systems, dashboards, and internal tools that optimize workflows.",
    },
    {
      title: "E-commerce Platforms",
      desc: "Secure, conversion-focused online stores with payment integration and inventory control.",
    },
    {
      title: "API and Integrations",
      desc: "Seamless integration with third-party services, payment gateways, and existing systems.",
    },
    {
      title: "Launch Your Next Product",
      desc: "From idea to MVP, we build focused digital products that are ready for real users.",
      image: overlayImage,
      cta: "Start a project",
      href: "#/contact_us/",
      featured: true,
    },
  ];

  return (
    <section className="appdev-build">
      <div className="container">
        <div className="appdev-build__header">
          <div className="appdev-build__badge">OUR EXPERTISE</div>
          <h2 className="appdev-build__title">What We Build</h2>
          <p className="appdev-build__subtitle">
            Flexible, reliable products designed around your users and your goals.
          </p>
        </div>

        <div className="row g-4">
          {builds.map((item, index) => (
            <BuildCard
              key={index}
              item={item}
              index={index}
            />
          ))}
        </div>
      </div>

      <style>{`
        .appdev-build {
          padding: 100px 0;
          background: #ffffff;
          overflow: hidden;
        }

        .appdev-build__header {
          max-width: 700px;
          margin-bottom: 60px;
        }

        .appdev-build__badge {
          display: inline-block;
          font-size: 0.7rem;
          font-weight: 800;
          letter-spacing: 2px;
          color: #e88a1c;
          margin-bottom: 15px;
        }

        .appdev-build__title {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 800;
          color: #1a1a2e;
          letter-spacing: -0.03em;
          line-height: 1.1;
        }

        .appdev-build__subtitle {
          font-size: 1.1rem;
          color: #64748b;
          margin-top: 15px;
        }

        .appdev-build-card {
          position: relative;
          padding: 40px;
          background: #f8f9fa;
          border-radius: 4px;
          transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
          border: 1px solid rgba(0, 0, 0, 0.05);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          opacity: 0;
          transform: translateY(20px);
        }

        .appdev-build-card--visible {
          opacity: 1;
          transform: translateY(0);
        }

        .appdev-build-card:hover {
          background: #ffffff;
          box-shadow: 0 30px 60px rgba(0, 0, 0, 0.08);
          transform: translateY(-5px);
          border-color: #e88a1c;
        }

        .appdev-build-card__index {
          font-size: 0.8rem;
          font-weight: 700;
          color: #cbd5e1;
          margin-bottom: 20px;
          display: block;
        }

        .appdev-build-card__title {
          font-weight: 700;
          font-size: 1.5rem;
          color: #1a1a2e;
          margin-bottom: 15px;
        }

        .appdev-build-card__desc {
          color: #64748b;
          line-height: 1.6;
          font-size: 0.95rem;
          margin-bottom: 0;
        }

        .appdev-build-card--featured {
          background: #1a1a2e;
          border: none;
          color: white;
          overflow: hidden;
        }

        .appdev-build-card--featured::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image: var(--card-img);
          background-size: cover;
          background-position: center;
          opacity: 0.3;
          transition: transform 0.5s ease;
        }

        .appdev-build-card--featured:hover::before {
          transform: scale(1.1);
        }

        .appdev-build-card--featured .appdev-build-card__title,
        .appdev-build-card--featured .appdev-build-card__desc {
          color: white;
          position: relative;
          z-index: 2;
        }

        .appdev-build-card--featured .appdev-build-card__index {
          color: rgba(255, 255, 255, 0.3);
          position: relative;
          z-index: 2;
        }

        .appdev-build-card__cta {
          margin-top: 30px;
          display: inline-flex;
          align-items: center;
          padding: 12px 25px;
          background: #e88a1c;
          color: white;
          font-weight: 700;
          font-size: 0.85rem;
          text-transform: uppercase;
          text-decoration: none;
          border-radius: 2px;
          position: relative;
          z-index: 2;
          transition: background 0.3s ease;
        }

        .appdev-build-card__cta:hover {
          background: #cf7610;
          color: white;
        }

        @media (max-width: 768px) {
          .appdev-build {
            padding: 60px 0;
          }

          .appdev-build-card {
            padding: 30px;
          }
        }
      `}</style>
    </section>
  );
}

function BuildCard({ item, index }) {
  const cardRef = useRef(null);

  useEffect(() => {
    if (!cardRef.current) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      cardRef.current.classList.add("appdev-build-card--visible");
      return undefined;
    }

    const scrollContainer = cardRef.current.closest(".body") ?? null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          cardRef.current?.classList.add("appdev-build-card--visible");
          observer.disconnect();
        }
      },
      {
        root: scrollContainer,
        threshold: 0.15,
      }
    );

    observer.observe(cardRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="col-md-6 col-lg-4">
      <article
        ref={cardRef}
        className={`appdev-build-card h-100 ${item.featured ? "appdev-build-card--featured" : ""}`}
        style={{
          "--card-img": item.image ? `url(${item.image})` : "none",
        }}
      >
        <div>
          <span className="appdev-build-card__index">{String(index + 1).padStart(2, "0")}</span>
          <h5 className="appdev-build-card__title">{item.title}</h5>
          <p className="appdev-build-card__desc">{item.desc}</p>
        </div>

        {item.cta && (
          <div>
            <a href={item.href} className="appdev-build-card__cta">
              {item.cta}
            </a>
          </div>
        )}
      </article>
    </div>
  );
}
